import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { Game } from '@Game/Game';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ILockAndRollSymbolData } from '@GFTypes/GameStoreTypes/SpinOutcome';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { takeAction } from '@Managers/ButtonManager/ButtonActions';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { runIf } from '@Tools/RunIf';
import { SpecialGamesSpinCounter } from '../SpecialGamesSpinCounter';
import { SpecialGamesTotalWin } from '../SpecialGamesTotalWin';

export class LockAndRoll extends GameComponent {
    private lockedSymbols: ILockAndRollSymbolData[] = [];
    private counter: SpecialGamesSpinCounter;
    private totalWin: SpecialGamesTotalWin;

    constructor(public readonly scene: GameScene) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, { class: LockAndRoll.name, source: LoggerSource.Game, message: 'LockAndRoll constructor' });
    }

    public onCreate(): void {
        this.setInitialCoinValues();
        this.counter = new SpecialGamesSpinCounter(this.scene, ESpecialGamesType.LOCK_AND_ROLL);
        this.totalWin = new SpecialGamesTotalWin(this.scene, ESpecialGamesType.LOCK_AND_ROLL);
        EventsCenter.on(EGameStates.POST_SPECIAL_GAMES_INTRO_STATE, this.onFeatureStart, this);
        EventsCenter.on(EGameStates.POST_SPIN_STATE, this.lockSymbols, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.onSpin, this);
        EventsCenter.on(EGameStates.COMPLETE_SPIN_STATE, this.onSpinComplete, this);
        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_OUTRO_STATE, this.unlockSymbols, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_RESTORE, this.onFeatureStart, this);
        EventsCenter.on(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, this.onPostWinPresentationState, this);
    }

    private setInitialCoinValues(): void {
        const isActive =
            this.gameStore.get(EGameStoreKeys.IsLockAndRollMode) ||
            (this.gameStore.get(EGameStoreKeys.IsGameRestored) && this.gameStore.get(EGameStoreKeys.LockAndRoll).totalSpins > 0);
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);

        visibleSymbols.forEach((reel) => {
            reel.forEach((symbol) => {
                symbol.setLockAndRollCoinValue(isActive);
            });
        });
    }

    @runIf((t: LockAndRoll) => t.gameStore.get(EGameStoreKeys.IsLockAndRollMode))
    private onFeatureStart(): void {
        this.counter.sync(true, true);
        this.lockSymbols();
    }

    @runIf((t: LockAndRoll) => t.gameStore.get(EGameStoreKeys.IsLockAndRollMode))
    private onSpin(): void {
        EventsCenter.emit(ALL_EVENTS_LIST.SPECIAL_GAMES_PLAYED_COUNTER_CHANGE);
    }

    @runIf((t: LockAndRoll) => t.gameStore.get(EGameStoreKeys.IsLockAndRollMode))
    private onSpinComplete(): void {
        EventsCenter.emit(ALL_EVENTS_LIST.SPECIAL_GAMES_TOTAL_COUNTER_CHANGE);
    }

    @runIf((t: LockAndRoll) => t.gameStore.get(EGameStoreKeys.IsLockAndRollMode))
    private lockSymbols(): void {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const symbols = this.gameStore.get(EGameStoreKeys.LockAndRoll).symbols;
        symbols.forEach((symbol) => {
            if (this.lockedSymbols.some((_symbol) => _symbol.reel === symbol.reel && _symbol.index === symbol.index) || !visibleSymbols[symbol.reel]) return;
            const reelSymbol = visibleSymbols[symbol.reel][symbol.index];
            this.scene.stickySymbols.lockSymbol(reelSymbol);
            this.lockedSymbols.push(symbol);
        });
    }

    @runIf((t: LockAndRoll) => t.gameStore.get(EGameStoreKeys.IsLockAndRollMode))
    private unlockSymbols(): void {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);

        this.lockedSymbols.forEach((symbol) => {
            if (!this.lockedSymbols.some((_symbol) => _symbol.reel === symbol.reel && _symbol.index === symbol.index)) return;
            const reelSymbol = visibleSymbols[symbol.reel][symbol.index];
            this.scene.stickySymbols.unlockSymbol(reelSymbol);
        });

        this.lockedSymbols = [];
    }

    @runIf((t: LockAndRoll) => {
        const isLockAndRollMode = t.gameStore.get(EGameStoreKeys.IsLockAndRollMode);
        const isPostWinState = t.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.POST_WIN_STATE;
        return isLockAndRollMode && isPostWinState;
    })
    private onPostWinPresentationState(): void {
        takeAction(this.scene.game as Game, false);
    }
}
